'use client';

/**
 * PanelAttributeList Component
 *
 * GitHub-style key-value attribute list for displaying item properties.
 * Supports icons, copyable values, links, and inline click-to-edit.
 */

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import CopyIcon from '@mui/icons-material/ContentCopy';
import EditPencilIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import {
  alpha,
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Link,
  ListItemButton,
  MenuItem,
  Popper,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import { styled, type Theme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n, useReducedMotion } from '../../hooks';
import { usePermissionOptional } from '../../providers/PermissionProvider';
import { withSoftBreakPoints } from '../../utils/textBreaks';
import { GlassTooltip } from '../GlassTooltip';
import { MoneyField } from '../MoneyField/MoneyField';
import { OverflowTooltip } from '../OverflowTooltip';

// ============================================================================
// Types
// ============================================================================

/**
 * Input type for inline editing.
 * - `text`: single-line text input
 * - `textarea`: multi-line text (blur to save — Enter inserts newline)
 * - `number`: numeric input
 * - `date`: native date picker (YYYY-MM-DD)
 * - `select`: static dropdown from `editOptions`
 * - `lookup`: searchable async popover picker driven by `lookup.search()`.
 *   Use for fields whose option set is too large to enumerate eagerly
 *   (e.g., borrower picker across thousands of consumers, assignee
 *   picker across dozens of team members with typeahead search).
 */
export type PanelAttributeEditType = 'text' | 'textarea' | 'number' | 'date' | 'select' | 'lookup' | 'money';

export interface PanelAttributeEditOption {
  value: string;
  label: string;
}

/**
 * Richer option shape used by `editType: 'lookup'`. In addition to
 * value/label, supports a subtitle (e.g., email under a person's name)
 * and an optional avatar node (initials, image, icon).
 */
export interface PanelAttributeLookupOption {
  value: string;
  label: string;
  subtitle?: string;
  avatar?: React.ReactNode;
}

/**
 * Configuration for `editType: 'lookup'` — an async popover picker.
 * The consumer owns the search implementation; the picker debounces,
 * shows a loading state, and renders results in a selectable list.
 */
export interface PanelAttributeLookupConfig {
  /**
   * Async search function. Called with the user's current query (may
   * be empty on open). Should return a short list of candidates — the
   * picker doesn't paginate, so cap to ~50 results server-side.
   */
  search: (query: string) => Promise<PanelAttributeLookupOption[]>;
  /**
   * Optional placeholder for the search input.
   * @default 'Search…'
   */
  placeholder?: string;
  /**
   * Minimum characters before firing search. Set to 0 to search on open
   * with an empty query (useful when the list is small enough to show
   * everything, like team members).
   * @default 0
   */
  minChars?: number;
  /**
   * Debounce in milliseconds between keystrokes before firing search.
   * @default 200
   */
  debounceMs?: number;
  /**
   * Optional label for the currently selected value, shown as a hint
   * above the search input. Helps users confirm what they're replacing.
   */
  currentLabel?: string;
  /**
   * If set, the picker renders a "Clear" button that fires onSave('').
   * Use for optional relations (e.g., assignee can be unassigned).
   */
  allowClear?: boolean;
  /** Copy for the Clear button. @default 'Clear' */
  clearLabel?: string;
}

export interface PanelAttribute {
  /** Unique key for edit-mode reconciliation. Defaults to `label`. */
  key?: string;
  /** Attribute label */
  label: string;
  /** Attribute value (can be any React node) */
  value: React.ReactNode;
  /** Optional icon to display before label */
  icon?: React.ReactNode;
  /** Make value copyable */
  copyable?: boolean;
  /** Value to copy (defaults to string value) */
  copyValue?: string;
  /** Make value a link */
  href?: string;
  /** Tooltip for the value */
  tooltip?: string;
  /** Hide this attribute */
  hidden?: boolean;

  // ------- Inline edit support -------
  /** Enable click-to-edit for this attribute. Requires `onSave`. */
  editable?: boolean;
  /** Input type in edit mode. Defaults to `text`. */
  editType?: PanelAttributeEditType;
  /**
   * Raw editable value (used to seed the input). For `date`, use an ISO string
   * (YYYY-MM-DD). For `select`, must match one of `editOptions[].value`.
   */
  editValue?: string | number;
  /** Options for `editType: 'select'`. */
  editOptions?: PanelAttributeEditOption[];
  /**
   * Configuration for `editType: 'lookup'`. Required when editType is
   * 'lookup'; ignored otherwise.
   */
  lookup?: PanelAttributeLookupConfig;
  /** Placeholder shown in the input when empty. */
  editPlaceholder?: string;
  /**
   * Async save handler. Called when the user confirms the edit (blur/enter/select change).
   * Throw or reject to keep the input open and show an error.
   */
  onSave?: (newValue: string) => void | Promise<void>;
  /**
   * Synchronous validator. Return an error message to block save, or null/undefined to accept.
   * Runs before `onSave` is called.
   */
  validate?: (newValue: string) => string | null | undefined;
  /**
   * Permission required to edit this attribute. When provided and the current user lacks it,
   * the row renders as read-only (no pencil icon, no click target). Uses the design system's
   * `usePermissionOptional()` so it's a no-op outside a PermissionProvider (tests, Storybook).
   */
  requiredPermission?: string;

  /**
   * Semantic requirement state — distinct from the transient inline-edit
   * state (`InlineInputState`). Drives:
   *   - a small red indicator dot next to the label (required+missing)
   *   - an error-toned helperText line below the row
   *   - error chrome on the inline input when the row goes editable
   *
   * Resolved upstream via `useFieldRequirementState` (in 10-business-portal/
   * src/hooks) which reads from `loanStageRequirements.checkExit`. Pass
   * undefined / `level: 'optional'` to render the row with no chrome.
   */
  requirementState?: PanelAttributeRequirementState;
}

/**
 * Requirement state for a single attribute. Mirrors `FieldRequirementState`
 * in the consuming app's hook so the API surface is symmetric. Tracks the
 * platform's 2-state requirement system (REQUIRED | OPTIONAL — PR #1529)
 * plus an `unknown` sentinel for first paint.
 */
export interface PanelAttributeRequirementState {
  /**
   *   - `required` → field MUST be filled to advance the loan; render error red
   *   - `optional` → in the catalog but not required for next stage; no chrome
   *   - `unknown`  → check-exit hasn't loaded; no chrome
   */
  level: 'required' | 'optional' | 'unknown';
  /** Whether the required item is currently missing on the case. */
  missing: boolean;
  /** Optional explanatory copy for the helperText slot. */
  helperText?: string | null;
}

export interface PanelAttributeListProps {
  /** List of attributes to display */
  attributes: PanelAttribute[];
  /** Show loading skeletons */
  loading?: boolean;
  /** Number of skeleton rows when loading */
  skeletonCount?: number;
  /** Compact mode with reduced spacing */
  compact?: boolean;
  /** Divider between items */
  dividers?: boolean;
  /**
   * Field layout.
   * - `stacked` (default): label on top, value below — arranged in a responsive
   *   grid. Best for dense forms with many fields.
   * - `row`: label on the left, value on the right — best for short attribute
   *   pairs in narrow sidebars (definition-list pattern).
   *
   * Both layouts share the same label typography (regular-weight mixed-case
   * muted) — the visual difference is positional, not stylistic.
   */
  layout?: 'row' | 'stacked';
  /**
   * Number of columns to use for `stacked` layout. Defaults to `2`. Ignored
   * when `layout === 'row'`.
   */
  columns?: 1 | 2;
}

// ============================================================================
// Styled Components
// ============================================================================

// Motion-enabled Box for animations
const MotionBox = motion.create(Box);

interface ListContainerProps {
  $layout: 'row' | 'stacked';
  $columns: 1 | 2;
  $compact: boolean;
}

const ListContainer = styled(MotionBox, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<ListContainerProps>(({ theme, $layout, $columns, $compact }) => {
  if ($layout === 'stacked') {
    return {
      display: 'grid',
      gridTemplateColumns: $columns === 2 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
      columnGap: theme.spacing(2),
      rowGap: theme.spacing($compact ? 1.25 : 1.75),
    };
  }
  return {
    display: 'flex',
    flexDirection: 'column',
  };
});

interface AttributeRowProps {
  $compact?: boolean;
  $hasDivider?: boolean;
  $layout: 'row' | 'stacked';
}

const AttributeRow = styled(MotionBox, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<AttributeRowProps>(({ theme, $compact, $hasDivider, $layout }) => {
  if ($layout === 'stacked') {
    return {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(0.25),
      minWidth: 0,
      // Dividers look noisy in a grid — skip unless explicitly enabled.
      borderBottom: $hasDivider ? `1px solid ${theme.palette.divider}` : 'none',
      paddingBottom: $hasDivider ? theme.spacing($compact ? 0.75 : 1) : 0,
      // Mirror the row-layout last-child override so the final grid item
      // doesn't render a trailing divider. For columns=2 with an even item
      // count the "last full row" case is more nuanced — deferred since
      // `dividers` is opt-in (default false).
      '&:last-child': {
        borderBottom: 'none',
        paddingBottom: 0,
      },
    };
  }
  return {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: theme.spacing($compact ? 1 : 1.5, 0),
    borderBottom: $hasDivider ? `1px solid ${theme.palette.divider}` : 'none',
    gap: theme.spacing(2),

    '&:last-child': {
      borderBottom: 'none',
    },
  };
});

const LabelSection = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $layout: 'row' | 'stacked' }>(({ theme, $layout }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  ...($layout === 'row' ? { flex: '0 0 auto', minWidth: 100, maxWidth: '40%' } : { minWidth: 0, width: '100%' }),
}));

const LabelIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  fontSize: '1rem',

  '& > svg': {
    fontSize: '1rem',
  },
}));

const ValueSection = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $layout: 'row' | 'stacked' }>(({ theme, $layout }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  flex: 1,
  minWidth: 0,
  ...($layout === 'row'
    ? { justifyContent: 'flex-end', textAlign: 'right' }
    : { justifyContent: 'flex-start', textAlign: 'left', width: '100%' }),
}));

/**
 * Inline marker rendered next to an attribute label when the field is
 * required-and-missing. Sized to read as a punctuation mark, not a status
 * badge — the row itself stays the visual focus.
 */
const RequirementMarker = styled('span', {
  shouldForwardProp: (prop) => prop !== '$tone',
})<{ $tone: 'error' }>(({ theme }) => ({
  display: 'inline-block',
  width: 6,
  height: 6,
  borderRadius: '50%',
  marginLeft: theme.spacing(0.75),
  verticalAlign: 'middle',
  backgroundColor: theme.palette.error.main,
  // Subtle outer ring for legibility on the dark detail-panel background.
  boxShadow: `0 0 0 2px ${alpha(theme.palette.error.main, 0.2)}`,
}));

/**
 * Helper text rendered under the value in error tone. Caption-sized so
 * it doesn't compete with the value, but always visible (no hover) so the
 * borrower-info section reads as a checklist of what's still needed.
 */
const RequirementHelperText = styled('span', {
  shouldForwardProp: (prop) => prop !== '$tone',
})<{ $tone: 'error' }>(({ theme }) => ({
  display: 'block',
  marginTop: theme.spacing(0.5),
  fontSize: '0.75rem',
  lineHeight: 1.3,
  color: theme.palette.error.main,
}));

const CopyButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  marginLeft: theme.spacing(0.5),
  opacity: 0,
  transition: 'opacity 150ms ease',
  color: theme.palette.text.secondary,

  '.attribute-row:hover &': {
    opacity: 1,
  },

  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor:
      theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.08),
  },
}));

// Allow up to two lines of wrap before truncating, and break long words
// (emails, IDs) at the boundary. The previous single-line ellipsis was
// hiding short names like "Rachel Martinez" or "marcus.thompson@..." in
// cards that had room for them.
const ValueText = styled(Typography)({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
  fontSize: '0.8125rem', // 13px — slightly tighter than body2 to fit more
  lineHeight: 1.35,
}) as typeof Typography;

const EditablePressable = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$active' && prop !== '$layout',
})<{ $active?: boolean; $layout?: 'row' | 'stacked' }>(({ theme, $active, $layout = 'stacked' }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: $layout === 'row' ? 'flex-end' : 'flex-start',
  gap: theme.spacing(0.5),
  borderRadius: theme.spacing(0.5),
  padding: theme.spacing(0.25, 0.75),
  margin: theme.spacing(-0.25, -0.75),
  cursor: $active ? 'default' : 'pointer',
  transition: 'background-color 120ms ease',
  minWidth: 0,
  maxWidth: '100%',

  '&:hover': {
    backgroundColor: $active
      ? 'transparent'
      : theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.05)
        : alpha(theme.palette.common.black, 0.04),
  },

  '& .edit-pencil': {
    opacity: 0,
    transition: 'opacity 120ms ease',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },

  '&:hover .edit-pencil': {
    opacity: $active ? 0 : 0.7,
  },
}));

type InlineInputState = 'idle' | 'saving' | 'success' | 'error';

const stateColor = (state: InlineInputState, theme: Theme) => {
  switch (state) {
    case 'error':
      return theme.palette.error.main;
    case 'success':
      return theme.palette.success.main;
    case 'saving':
    case 'idle':
    default:
      return theme.palette.primary.main;
  }
};

const InlineInput = styled(InputBase, {
  shouldForwardProp: (prop) => prop !== '$state' && prop !== '$layout',
})<{ $state?: InlineInputState; $layout?: 'row' | 'stacked' }>(({ theme, $state = 'idle', $layout = 'stacked' }) => {
  const color = stateColor($state, theme);
  return {
    flex: 1,
    minWidth: 0,
    transition: 'opacity 160ms ease',
    opacity: $state === 'success' ? 0.85 : 1,
    '& .MuiInputBase-input': {
      padding: theme.spacing(0.25, 0.5),
      fontSize: '0.875rem',
      fontWeight: 500,
      textAlign: $layout === 'row' ? 'right' : 'left',
      borderRadius: theme.spacing(0.5),
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.06)
          : alpha(theme.palette.common.black, 0.04),
      border: `1px solid ${color}`,
      outline: 'none',
      transition: 'border-color 160ms ease, box-shadow 160ms ease',
      '&:focus': {
        borderColor: color,
        boxShadow: `0 0 0 2px ${alpha(color, 0.2)}`,
      },
      '&.Mui-disabled': {
        WebkitTextFillColor: theme.palette.text.primary,
      },
    },
    '& textarea.MuiInputBase-input': {
      textAlign: 'left',
      minHeight: '2.5em',
    },
  };
});

// Row layout aligns edit affordances (spinner, checkmark, select) to the
// right edge to match the right-aligned value column; stacked layout aligns
// them to the left so they sit under the label.
const editAlign = ($layout: 'row' | 'stacked') =>
  $layout === 'row'
    ? ({ justify: 'flex-end', textAlign: 'right' } as const)
    : ({ justify: 'flex-start', textAlign: 'left' } as const);

// ============================================================================
// EditableAttributeValue — internal sub-component for inline editing
// ============================================================================

// ============================================================================
// LookupPicker — async searchable popover for `editType: 'lookup'`
// ============================================================================

interface LookupPickerProps {
  label: string;
  config: PanelAttributeLookupConfig;
  saving: boolean;
  pending: boolean;
  error: string | null;
  /** Anchor element for the Popper — the pressable trigger from the parent. */
  anchorRef: React.RefObject<HTMLElement | null>;
  /** Called when the user picks an option (or clears). */
  onSelect: (value: string) => void;
  /** Called when the user dismisses without picking (Escape or click away). */
  onCancel: () => void;
}

const LookupPopper = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.modal + 1,
}));

const LookupPanel = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  minWidth: 320,
  maxWidth: 420,
  maxHeight: 400,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[8],
  overflow: 'hidden',
}));

const LookupSearchRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const LookupResultsList = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
});

const LookupOptionRow = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active?: boolean }>(({ theme, $active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 1.5),
  backgroundColor: $active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
  },
}));

const LookupFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(0.75, 1.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(1),
}));

const LookupPicker: React.FC<LookupPickerProps> = ({
  label,
  config,
  saving,
  pending,
  error,
  anchorRef,
  onSelect,
  onCancel,
}) => {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PanelAttributeLookupOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Monotonic counter to discard stale search responses that land out of order.
  const searchSeq = useRef(0);

  const minChars = config.minChars ?? 0;
  const debounceMs = config.debounceMs ?? 200;
  const isLocked = saving || pending;

  // Focus the search input on mount.
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Debounced search. Fires on mount (empty query) if minChars === 0.
  useEffect(() => {
    if (query.length < minChars) {
      setResults([]);
      setLoading(false);
      return;
    }
    const seq = ++searchSeq.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const next = await config.search(query);
        if (searchSeq.current === seq) {
          setResults(next);
          setHighlightedIndex(0);
        }
      } catch {
        if (searchSeq.current === seq) {
          setResults([]);
        }
      } finally {
        if (searchSeq.current === seq) {
          setLoading(false);
        }
      }
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [query, config.search, minChars, debounceMs, config]);

  // Click-away detector. Closes the picker when the user clicks outside
  // the popper and the anchor (the anchor already handled the click that
  // opened this picker).
  useEffect(() => {
    const handleClickAway = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onCancel();
      }
    };
    // Use a microtask delay so the opening click doesn't immediately close us.
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickAway);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickAway);
    };
  }, [anchorRef, onCancel]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((idx) => Math.min(idx + 1, results.length - 1));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((idx) => Math.max(idx - 1, 0));
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const picked = results[highlightedIndex];
        if (picked) onSelect(picked.value);
      }
    },
    [results, highlightedIndex, onSelect, onCancel],
  );

  // Render the popover via Popper anchored to the trigger.
  return (
    <>
      {/* Show a compact read-only placeholder on the row while the picker is open,
          so the row height doesn't collapse to nothing. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
        {isLocked && <CircularProgress size={14} aria-label={t('common.saving', 'Saving')} />}
        {pending && !saving && (
          <CheckCircleIcon
            fontSize="small"
            aria-label={t('common.saved', 'Saved')}
            sx={{ color: 'success.main', fontSize: 16 }}
          />
        )}
      </Box>
      <LookupPopper
        open
        anchorEl={anchorRef.current}
        placement="bottom-end"
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
      >
        <LookupPanel ref={panelRef} role="dialog" aria-label={t('common.edit', 'Edit') + ' ' + label}>
          <LookupSearchRow>
            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <InputBase
              inputRef={searchInputRef}
              value={query}
              placeholder={config.placeholder ?? t('common.search', 'Search…')}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLocked}
              sx={{ flex: 1, fontSize: '0.875rem' }}
            />
            <IconButton size="small" onClick={onCancel} aria-label={t('common.cancel', 'Cancel')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </LookupSearchRow>

          {config.currentLabel && (
            <Box sx={{ px: 1.5, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('common.current', 'Current')}: {config.currentLabel}
              </Typography>
            </Box>
          )}

          <LookupResultsList>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {!loading && results.length === 0 && query.length >= minChars && (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {query ? t('common.noResults', 'No results') : t('common.startTyping', 'Start typing to search…')}
                </Typography>
              </Box>
            )}
            {!loading &&
              results.map((opt, idx) => (
                <LookupOptionRow
                  key={opt.value}
                  $active={idx === highlightedIndex}
                  onClick={() => onSelect(opt.value)}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  disabled={isLocked}
                >
                  {opt.avatar && <Box sx={{ flexShrink: 0 }}>{opt.avatar}</Box>}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {opt.label}
                    </Typography>
                    {opt.subtitle && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {opt.subtitle}
                      </Typography>
                    )}
                  </Box>
                </LookupOptionRow>
              ))}
          </LookupResultsList>

          {config.allowClear && (
            <LookupFooter>
              <Box />
              <Typography
                component="button"
                variant="caption"
                onClick={() => onSelect('')}
                sx={{
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  color: 'error.main',
                  p: 0.5,
                  '&:hover': { textDecoration: 'underline' },
                }}
                disabled={isLocked}
              >
                {config.clearLabel ?? t('common.clear', 'Clear')}
              </Typography>
            </LookupFooter>
          )}

          {error && (
            <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="error" role="alert">
                {error}
              </Typography>
            </Box>
          )}
        </LookupPanel>
      </LookupPopper>
    </>
  );
};

interface EditableAttributeValueProps {
  attribute: PanelAttribute;
  /** Layout mode — determines text/control alignment in edit mode. */
  layout: 'row' | 'stacked';
  /** Render function for the read-only value (reuses the existing JSX). */
  renderReadOnly: () => React.ReactNode;
}

const toEditString = (raw: string | number | undefined): string => {
  if (raw === undefined || raw === null) return '';
  return String(raw);
};

/** Max time to keep the "pending" (optimistic) state visible before forcing exit. */
const PENDING_TIMEOUT_MS = 2000;

const EditableAttributeValue: React.FC<EditableAttributeValueProps> = ({ attribute, layout, renderReadOnly }) => {
  const { editType = 'text', editOptions, onSave, validate, editPlaceholder } = attribute;
  const { t } = useI18n();
  const permission = usePermissionOptional();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<string>(toEditString(attribute.editValue));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Value that was just saved but hasn't yet been reflected back in the
   * `attribute.editValue` prop (parent refetch in flight). While pending, we
   * keep the input mounted in a success-disabled state so the UI never flashes
   * the stale value between `onSave` resolving and the refetch completing.
   */
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  /**
   * Snapshot of `toEditString(attribute.editValue)` at the moment `onSave` was
   * invoked. We exit pending as soon as `editValue` DIFFERS from this snapshot,
   * regardless of whether the new value exactly matches `pendingValue`. This
   * handles server-side normalization (case folding, date format, trimming)
   * that would otherwise make the exact-equality check fall through to the
   * 2-second fallback timeout.
   */
  const preSaveValueRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  // Track previous edit state so we can restore focus when exiting edit mode
  const wasEditing = useRef(false);
  // Guard against double-save from Enter+blur firing back-to-back
  const savingGuard = useRef(false);
  // Synchronous mirror of `isEditing` for the blur handler: `setIsEditing(false)`
  // from Escape/cancel is async, but blur fires on unmount with the stale render
  // closure, which would otherwise race and commit a cancelled edit.
  const isEditingRef = useRef(false);

  // Sync draft from prop when not editing (keeps read-only state consistent)
  useEffect(() => {
    if (!isEditing) setDraft(toEditString(attribute.editValue));
  }, [attribute.editValue, isEditing]);

  // Mirror isEditing into the ref. cancelEdit/enterEdit already update the ref
  // synchronously for the blur race; this effect catches pending-lifecycle and
  // commit() transitions that only touch state.
  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  // Autofocus + select text on entering edit mode
  useEffect(() => {
    if (isEditing && !pendingValue && inputRef.current) {
      inputRef.current.focus();
      if ('select' in inputRef.current) inputRef.current.select();
    }
  }, [isEditing, pendingValue]);

  // Restore focus to the trigger when leaving edit mode (a11y)
  useEffect(() => {
    if (wasEditing.current && !isEditing) {
      triggerRef.current?.focus();
    }
    wasEditing.current = isEditing;
  }, [isEditing]);

  /**
   * Optimistic pending lifecycle:
   *  - While `pendingValue !== null`, the input is mounted as success-disabled.
   *  - When the prop `editValue` changes AT ALL from its pre-save snapshot →
   *    parent refetch landed, so we collapse into read-only. This tolerates
   *    server-side normalization (case folding, date reformat, trimming) that
   *    would break an exact-equality check.
   *  - Fallback: force-exit after `PENDING_TIMEOUT_MS` so the UI never gets
   *    stuck if the consumer never propagates the change.
   */
  useEffect(() => {
    if (pendingValue === null || preSaveValueRef.current === null) return;
    const currentEditValue = toEditString(attribute.editValue);
    if (currentEditValue !== preSaveValueRef.current) {
      setPendingValue(null);
      setIsEditing(false);
      preSaveValueRef.current = null;
      return;
    }
    const timer = setTimeout(() => {
      setPendingValue(null);
      setIsEditing(false);
      preSaveValueRef.current = null;
    }, PENDING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [attribute.editValue, pendingValue]);

  const enterEdit = useCallback(() => {
    if (saving || pendingValue !== null) return;
    setDraft(toEditString(attribute.editValue));
    setError(null);
    isEditingRef.current = true;
    setIsEditing(true);
  }, [attribute.editValue, saving, pendingValue]);

  const cancelEdit = useCallback(() => {
    setPendingValue(null);
    preSaveValueRef.current = null;
    isEditingRef.current = false;
    setIsEditing(false);
    setError(null);
    setDraft(toEditString(attribute.editValue));
  }, [attribute.editValue]);

  const commit = useCallback(
    async (nextValue: string) => {
      if (savingGuard.current) return;
      // No-op if value unchanged
      if (nextValue === toEditString(attribute.editValue)) {
        setIsEditing(false);
        setError(null);
        return;
      }
      if (validate) {
        const msg = validate(nextValue);
        if (msg) {
          setError(msg);
          return;
        }
      }
      if (!onSave) {
        setIsEditing(false);
        return;
      }
      // Snapshot the editValue BEFORE awaiting onSave, so the pending-exit
      // useEffect has a stable "before" to compare against (the server may
      // return a normalized value that wouldn't match pendingValue exactly).
      preSaveValueRef.current = toEditString(attribute.editValue);
      savingGuard.current = true;
      setSaving(true);
      setError(null);
      try {
        await onSave(nextValue);
        // Enter pending state — keep the input visible until parent prop catches up.
        setPendingValue(nextValue);
      } catch (err) {
        preSaveValueRef.current = null;
        setError(err instanceof Error ? err.message : 'Failed to save');
      } finally {
        setSaving(false);
        savingGuard.current = false;
      }
    },
    [attribute.editValue, onSave, validate],
  );

  // Derived state for nice UX: idle → saving → success/error
  const inputState: InlineInputState = error ? 'error' : saving ? 'saving' : pendingValue !== null ? 'success' : 'idle';

  // Permission gate: if the attribute declares a required permission and the
  // current user lacks it (and we're inside a PermissionProvider), render as
  // read-only. Outside a provider (tests, Storybook) `permission` is null and
  // we fall through to the editable path.
  const isPermitted =
    !attribute.requiredPermission || permission === null || permission.can(attribute.requiredPermission);

  if (!isPermitted) {
    return <>{renderReadOnly()}</>;
  }

  if (!isEditing) {
    return (
      <EditablePressable
        ref={triggerRef}
        $layout={layout}
        role="button"
        tabIndex={0}
        aria-label={t('common.edit', 'Edit') + ' ' + attribute.label}
        onClick={enterEdit}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            enterEdit();
          }
        }}
      >
        {renderReadOnly()}
        <EditPencilIcon className="edit-pencil" fontSize="inherit" />
      </EditablePressable>
    );
  }

  const isLocked = saving || pendingValue !== null;
  const { justify, textAlign } = editAlign(layout);

  if (editType === 'lookup' && attribute.lookup) {
    return (
      <>
        {/* Keep trigger mounted so the Popper has a valid anchor element */}
        <Box ref={triggerRef} sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {renderReadOnly()}
        </Box>
        <LookupPicker
          label={attribute.label}
          config={attribute.lookup}
          saving={saving}
          pending={pendingValue !== null}
          error={error}
          anchorRef={triggerRef}
          onSelect={(nextValue) => {
            void commit(nextValue);
          }}
          onCancel={cancelEdit}
        />
      </>
    );
  }

  if (editType === 'select') {
    const options = editOptions ?? [];
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.5, minWidth: 120 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: justify }}>
          <Select
            // Keep dropdown open while the user is choosing; close it once we enter
            // the pending/saving phase so the UI reads as "committed".
            open={!isLocked}
            value={draft}
            disabled={isLocked}
            variant="standard"
            disableUnderline
            onClose={() => {
              // Only treat close as cancel while we haven't saved yet.
              if (!isLocked && error === null) cancelEdit();
            }}
            onChange={(event) => {
              const next = String(event.target.value);
              setDraft(next);
              void commit(next);
            }}
            sx={{
              fontSize: '0.8125rem',
              '& .MuiSelect-select': {
                padding: '2px 4px',
                textAlign,
              },
              ...(inputState === 'success' && { opacity: 0.85 }),
            }}
          >
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {saving && <CircularProgress size={14} aria-label={t('common.saving', 'Saving')} />}
          {pendingValue !== null && !saving && (
            <CheckCircleIcon
              fontSize="small"
              aria-label={t('common.saved', 'Saved')}
              sx={{ color: 'success.main', fontSize: 16 }}
            />
          )}
        </Box>
        {error && (
          <Typography variant="caption" color="error" sx={{ textAlign }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  if (editType === 'money') {
    // MoneyField commits on blur via its own `onChange` callback (the canonical
    // digit string). Route through `commit()` so `validate` / `onSave` /
    // saving-state work the same as the text path.
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.5, flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: justify }}>
          <MoneyField
            size="small"
            autoFocus
            value={draft}
            placeholder={editPlaceholder}
            disabled={isLocked}
            onChange={(next) => {
              setDraft(next);
              if (error) setError(null);
              void commit(next);
            }}
            error={Boolean(error)}
            sx={{ width: 160 }}
          />
          {saving && <CircularProgress size={14} aria-label={t('common.saving', 'Saving')} />}
          {pendingValue !== null && !saving && (
            <CheckCircleIcon
              fontSize="small"
              aria-label={t('common.saved', 'Saved')}
              sx={{ color: 'success.main', fontSize: 16 }}
            />
          )}
        </Box>
        {error && (
          <Typography variant="caption" color="error" sx={{ textAlign }} role="alert">
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  const multiline = editType === 'textarea';
  const inputType = editType === 'number' ? 'number' : editType === 'date' ? 'date' : 'text';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.5, flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: justify }}>
        <InlineInput
          $state={inputState}
          $layout={layout}
          inputRef={inputRef}
          value={draft}
          type={inputType}
          multiline={multiline}
          minRows={multiline ? 2 : undefined}
          maxRows={multiline ? 6 : undefined}
          placeholder={editPlaceholder}
          disabled={isLocked}
          onChange={(event) => {
            setDraft(event.target.value);
            // Typing clears any prior error so the user can retry
            if (error) setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              cancelEdit();
              return;
            }
            if (event.key === 'Enter' && !multiline) {
              event.preventDefault();
              void commit(draft);
            }
          }}
          onBlur={() => {
            // Small delay so Escape keydown can win the race. Read isEditing
            // from the ref (not the closure) so a cancelled edit — which sets
            // isEditingRef.current=false synchronously in cancelEdit — isn't
            // silently committed when blur fires on unmount.
            setTimeout(() => {
              if (!savingGuard.current && !isLocked && isEditingRef.current) void commit(draft);
            }, 0);
          }}
        />
        {saving && <CircularProgress size={14} aria-label={t('common.saving', 'Saving')} />}
        {pendingValue !== null && !saving && (
          <CheckCircleIcon
            fontSize="small"
            aria-label={t('common.saved', 'Saved')}
            sx={{ color: 'success.main', fontSize: 16 }}
          />
        )}
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ textAlign }} role="alert">
          {error}
        </Typography>
      )}
    </Box>
  );
};

// ============================================================================
// Animation
// ============================================================================

const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0.05,
    },
  },
};

// Bezier easing curve (typed as tuple for framer-motion compatibility)
const easeOutCubic: [number, number, number, number] = [0.215, 0.61, 0.355, 1];

const itemVariants = {
  hidden: { opacity: 0, x: 10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.15,
      ease: easeOutCubic,
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export const PanelAttributeList: React.FC<PanelAttributeListProps> = ({
  attributes,
  loading = false,
  skeletonCount = 5,
  compact = false,
  dividers = false,
  layout = 'stacked',
  columns = 2,
}) => {
  const reducedMotion = useReducedMotion();
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = useCallback(async (value: string, index: number) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  // Both layouts use the same label treatment — regular-weight mixed-case
  // muted text at 13px — so detail panels on the same page read consistently
  // regardless of whether they're row- or stacked-layout. The previous
  // stacked variant ("uppercase 600 / 11px / wider tracking") shouted next
  // to row-layout labels on the same page, and the previous size-branching
  // (`compact ? 13px : 14px` for label, plus `stacked || !compact ? 14px : 13px`
  // for value) made stacked cards visibly larger than row cards even when
  // they shared the page — see the Loan Information vs Live Sizing pair
  // on the loan-detail screen. Color (`text.secondary` vs the value's
  // `text.primary`) provides the label/value differentiation; in stacked
  // layout the position-above-value already signals "this is the label."
  const labelSx = {
    color: 'text.secondary',
    fontWeight: 500,
    // 12px (fontSizes.xs floor per portal rules) — smaller than the 13px
    // value so the label/value hierarchy reads at a glance. Stops feeling
    // like both are competing for attention in dense panels (Loan
    // Information, Borrower, etc.).
    fontSize: '0.75rem',
    lineHeight: 1.2,
  };

  const valueFontSize = '0.8125rem';

  // Loading state
  if (loading) {
    return (
      <ListContainer $layout={layout} $columns={columns} $compact={compact}>
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <AttributeRow key={index} $compact={compact} $hasDivider={dividers} $layout={layout}>
            <LabelSection $layout={layout}>
              <Skeleton variant="text" width={80} />
            </LabelSection>
            <ValueSection $layout={layout}>
              <Skeleton variant="text" width={120} />
            </ValueSection>
          </AttributeRow>
        ))}
      </ListContainer>
    );
  }

  // Filter out hidden attributes
  const visibleAttributes = attributes.filter((attr) => !attr.hidden);

  if (visibleAttributes.length === 0) {
    return (
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          No attributes to display
        </Typography>
      </Box>
    );
  }

  return (
    <ListContainer
      $layout={layout}
      $columns={columns}
      $compact={compact}
      variants={reducedMotion ? undefined : listVariants}
      initial={reducedMotion ? undefined : 'hidden'}
      animate={reducedMotion ? undefined : 'visible'}
    >
      {visibleAttributes.map((attr, index) => {
        const copyValue = attr.copyValue ?? (typeof attr.value === 'string' ? attr.value : undefined);
        const rowKey = attr.key ?? `${attr.label}-${index}`;

        // attr.tooltip stays unconditional (pre-existing API contract);
        // unset values fall back to OverflowTooltip's auto title (which keeps
        // the original unbroken string for hover readability).
        const autoOverflowTitle: React.ReactNode = typeof attr.value === 'string' ? attr.value : undefined;
        // For string values, inject HTML word-break opportunities (`<wbr/>`)
        // after natural break chars (@, ., -, _, /). This makes long
        // unbreakable words like emails (`marcus.thompson@investorpro.com`)
        // and IDs wrap at predictable points instead of mid-character when
        // they don't fit the value column. No-op for short strings; ReactNode
        // values render unchanged (caller's responsibility).
        const renderedValue: React.ReactNode =
          typeof attr.value === 'string' ? withSoftBreakPoints(attr.value) : attr.value;
        const renderReadOnlyValue = () => {
          const textNode = attr.href ? (
            <ValueText
              component={Link}
              href={attr.href}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{
                fontSize: valueFontSize,
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
                display: 'block',
              }}
            >
              {renderedValue}
            </ValueText>
          ) : (
            <ValueText
              component="div"
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: valueFontSize,
              }}
            >
              {renderedValue}
            </ValueText>
          );

          if (attr.tooltip) {
            return (
              <GlassTooltip title={attr.tooltip} arrow placement="top">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>{textNode}</Box>
              </GlassTooltip>
            );
          }
          return (
            <OverflowTooltip title={autoOverflowTitle} disabled={!autoOverflowTitle}>
              {textNode}
            </OverflowTooltip>
          );
        };

        return (
          <AttributeRow
            key={rowKey}
            className="attribute-row"
            $compact={compact}
            $hasDivider={dividers}
            $layout={layout}
            variants={reducedMotion ? undefined : itemVariants}
          >
            <LabelSection $layout={layout}>
              {attr.icon && <LabelIcon>{attr.icon}</LabelIcon>}
              <Typography variant="body2" sx={labelSx}>
                {attr.label}
                {attr.requirementState?.missing && attr.requirementState.level === 'required' ? (
                  <GlassTooltip title={attr.requirementState.helperText ?? 'Required'} arrow placement="top">
                    <RequirementMarker
                      $tone="error"
                      aria-label={`${attr.label} is required`}
                      data-testid="panel-attr-required-marker"
                    />
                  </GlassTooltip>
                ) : null}
              </Typography>
            </LabelSection>
            <ValueSection $layout={layout}>
              {attr.editable && attr.onSave ? (
                <EditableAttributeValue attribute={attr} layout={layout} renderReadOnly={renderReadOnlyValue} />
              ) : (
                renderReadOnlyValue()
              )}

              {attr.copyable && copyValue && (
                <GlassTooltip title={copiedIndex === index ? 'Copied!' : 'Copy'} arrow placement="top">
                  <CopyButton
                    size="small"
                    onClick={() => handleCopy(copyValue, index)}
                    aria-label={`Copy ${attr.label}`}
                  >
                    <CopyIcon sx={{ fontSize: '0.875rem' }} />
                  </CopyButton>
                </GlassTooltip>
              )}

              {attr.requirementState?.missing &&
              attr.requirementState.level === 'required' &&
              attr.requirementState.helperText ? (
                <RequirementHelperText $tone="error" data-testid="panel-attr-helper-text">
                  {attr.requirementState.helperText}
                </RequirementHelperText>
              ) : null}
            </ValueSection>
          </AttributeRow>
        );
      })}
    </ListContainer>
  );
};

export default PanelAttributeList;
